import { FC, useState } from "react";
import toast from "react-hot-toast";
import { cancelReservation, Body } from "services/cancel-reservation";
import { CancellationPolicy } from "services/fetch-reservation";
import { Formatter } from "utils/formatters";
import { FaCalendarCheck } from 'react-icons/fa';
import { Ticket } from "./icons/Ticket";
import Trash from "./icons/Trash";
import { parseISO } from 'date-fns';

interface FormReservationProps {
  dateCheckin: string;
  dateCheckout: string;
  serviceId: number;
  reservationId: number;
  linkVoucherReservation?: string;
  isCancelled?: boolean;
  cancellationPolicies: CancellationPolicy[]
  bookingId: string
}

export const FormReservation: FC<FormReservationProps> = ({
  dateCheckin,
  dateCheckout,
  serviceId,
  reservationId,
  linkVoucherReservation,
  isCancelled = false,
  cancellationPolicies,
  bookingId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const dateCancellation = cancellationPolicies[0]?.end_date?.split(" ")[0];
  const datetimeCancellation = cancellationPolicies[0]?.end_date?.split(" ")[1];
  const startDateCancellation = new Date(cancellationPolicies[0]?.start_date)
  const dataAtual = new Date()
  const dataAtualString = new Date().toISOString().slice(0, 10);

  const handleCancelReservation = async (
    booking_id: string,
    data: Body
  ) => {
    setIsLoading(true);

    const response = cancelReservation(booking_id, data);
    toast.promise(
      response,
      {
        loading: "Cancelando reserva...",
        success: () => {
          window.location.reload();
          setShowModalConfirmation(false);
          setIsLoading(false);
          return "Reserva cancelada com sucesso!";
        },
        error: () => {
          setIsLoading(false);
          return "Erro ao cancelar reserva!";
        },
      },
      {
        style: {
          minWidth: "280px",
        },
      }
    );
  };

  if (isLoading) {
    return (
      <tr className="border-b border-neutral-200 dark:border-neutral-800">
        <td
          colSpan={4}
          className="text-center text-lg font-medium text-neutral-700 px-4 py-2 dark:text-white"
        >
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              ></path>
            </svg>
          </span>
        </td>
      </tr>
    );
  }

  return (
    <tr className="mx-auto rounded-xl md:rounded-none shadow-lg md:shadow-none border-b border-neutral-200/70 dark:border-neutral-800 w-full px-2">
      <td className="text-left md:text-center text-lg font-medium text-neutral-700 space-y-2 dark:text-white">
        <span className="text-xl mr-2 text-left font-semibold text-neutral-700 dark:text-white md:hidden">
          Reserva ID:
        </span>
        {reservationId}
        <div className="text-sm mr-2 text-neutral-400 dark:text-white md:hidden flex gap-1">
          <FaCalendarCheck className="mr-1" size={18} />
          <span>
            {Formatter.prettyDateBRFormated(dateCheckin)}
          </span>{"-"}
          <span>
            {Formatter.prettyDateBRFormated(dateCheckout)}
          </span>
        </div>
      </td>
      <td className="text-left md:text-center mx-auto hidden text-sm font-medium text-neutral-700 px-4 py-2 dark:text-white md:table-cell">
        <div className="flex gap-2 items-center justify-center">
          <FaCalendarCheck size={18} />
          {Formatter.prettyDateBRFormated(dateCheckin)}
        </div>
      </td>
      <td className="text-left md:text-center mx-auto hidden text-sm font-medium text-neutral-700 px-4 py-2 dark:text-white md:table-cell">
        <div className="flex gap-2 items-center justify-center">
          <FaCalendarCheck size={18} />
          {Formatter.prettyDateBRFormated(dateCheckout)}
        </div>
      </td>
      {isCancelled === false && (
        <td className="text-right text-sm font-medium text-neutral-700 w-full py-2 md:w-auto">
          <div className="flex flex-col md:flex-row items-center gap-2 justify-center mx-auto w-full md:w-1/2">
            {parseISO(dataAtualString) <= parseISO(dateCheckin) && (
              <button
                onClick={() => setShowModalConfirmation(true)}
                rel="noreferrer"
                className="bg-transparent border border-neutral-300 flex justify-center items-center rounded-lg w-full text-neutral-700 px-4 py-2 dark:bg-neutral-700 dark:hover:bg-neutral-800 dark:text-white dark:border-neutral-800"
              >
                <Trash className="h-6 mr-1 text-black dark:text-white" />
                Cancelar
              </button>
            )}
            {linkVoucherReservation && (
              <a
                href={linkVoucherReservation}
                target="_blank"
                className="bg-blue text-center flex justify-center items-center text-white w-full px-4 py-2 rounded-lg dark:bg-blue-600 dark:hover:bg-blue-700" rel="noreferrer"
              >
                <Ticket color="#fff" className="h-6 w-6 mr-1" />
                Voucher
              </a>
            )}
          </div>
        </td>
      )}

      {showModalConfirmation ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-2">
          <div className="flex flex-col py-4 md:justify-around bg-white dark:bg-neutral-900 shadow-xl rounded-3xl md:w-[805px] md:h-[300px]">
            <h1 className="md:text-2xl px-2 text-lg text-center md:text-start flex justify-center font-medium leading-5 text-neutral-700 dark:text-white mt-8 mb-4">
              Você tem certeza que deseja cancelar sua reserva?
            </h1>
            {cancellationPolicies && dataAtual >= startDateCancellation ? (
              <div className="flex flex-col items-center justify-center px-4">
                <p className="text-[15px] text-center font-medium leading-6 text-neutral-700 dark:text-white mb-4 break-words pl-[40px] pr-[40px] mt-[13px]">
                  Atenção, ao confirmar o cancelamento, será aplicada a multa conforme a política de cancelamento do hotel:
                </p>
                <span className="md:font-medium text-center text-lg leading-6 text-neutral-700 dark:text-white mb-4">
                  {`"A partir de ${Formatter.prettyDateBRFormated(dateCancellation)}
                    ${!datetimeCancellation.startsWith("00:00")
                      ? `ás ${datetimeCancellation.substring(0, 5)}`
                      : ""}
                    multa de `}
                  <strong>
                    {cancellationPolicies[0]?.value?.value > 1
                      ? `${new Intl.NumberFormat('pt-BR').format(cancellationPolicies[0]?.value?.value)} diárias."`
                      : `${new Intl.NumberFormat('pt-BR').format(cancellationPolicies[0]?.value?.value)} diária."`}
                  </strong>
                </span>
              </div>
            ) : null}
            <div className="md:flex md:flex-row flex-col gap-4 justify-end px-6">
              <button
                type="button"
                onClick={() => setShowModalConfirmation(false)}
                className="my-3 md:mb-5 w-full h-12 rounded-3xl whitespace-nowrap border border-gray-400 px-4 py-2 bg-white text-black"
              >
                Não, continuar com a reserva
              </button>
              <button
                type="button"
                onClick={() => handleCancelReservation(bookingId, {
                  broken_service_id: Number(serviceId),
                  penalty: dataAtual >= startDateCancellation ? cancellationPolicies[0]?.value?.value : 0
                })}
                className="my-3 md:mb-5 w-full whitespace-nowrap h-12 rounded-3xl px-4 py-2 bg-red-500 text-white hover:bg-blue-dark"
              >
                Sim, cancelar reserva
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </tr>
  );
};